
import Vue from 'vue';
import { mapInformationManagerUsers } from '../utils/informationManager';
import {
  ERROR_MESSAGES,
  CYCLE_MESSAGES
} from '../utils/constants';
import { commitInfoOwnerLists, normalizeInfoOwnerListPayload } from '../views/Project/InformationRequests/utils/infoOwner';

// Lazy accessor for Vue.axios to avoid race condition with plugin initialization
function getAxios() {
  return Vue.axios;
}

const BASE_URL = '/v2/financialclose/pbc/';

const baseurl = process.env.VUE_APP_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

function isPbcProjectNotFoundMessage(message, code) {
  return message === ERROR_MESSAGES.PBC_PROJECT_NOT_FOUND && Number(code) === 404;
}

/** True for getrequests failure body or HTTP 404 with "PBC project not found". */
function isPbcProjectNotFoundResponse(data, httpStatus) {
  const message = data?.message;
  const code = data?.code ?? httpStatus;
  return isPbcProjectNotFoundMessage(message, code);
}

/** Keep a rejected request from discarding a sibling call (e.g. infoowner/list). */
function settleAxios(promise) {
  return promise.then(
    (res) => ({ ok: true, res }),
    (error) => ({ ok: false, error }),
  );
}

/**
 * Build deep link for a tagged user — info owners get the requestee portal URL.
 * @param {Object} params
 * @returns {string}
 */
function buildTaggedUserRequestUrl({ userId, client, cycleId, requestId, pbcProjectId, infoOwners }) {
  const owner = (infoOwners || []).find((o) => String(o._id) === String(userId));
  if (owner?.code) {
    return `${baseurl}/pbc/requestee/project/${client}/${owner.code}/${pbcProjectId}`;
  }
  return `${baseurl}/financialclose/project/informationrequests/${cycleId}/request/${requestId}?cid=${client}`;
}

/** In-flight promise guards — concurrent dispatchers share one network round-trip. */
let fetchProjectContextInFlight = null;
let fetchProjectContextInFlightKey = null;
let fetchRequestsInFlight = null;
let fetchRequestsInFlightKey = null;

/**
 * Body fields for Phase E fcAccess / attachFcCycleId (cycle or linked PBC project).
 * Prefer cycleId when the Information Requests screen has a bookclosure cycle;
 * otherwise send pbcproject. Omit both when the screen has neither.
 * @param {{ projectId?: string, pbcprojectId?: string }} state
 * @param {Object} [extra]
 * @returns {Object}
 */
function withFcCycleContext(state, extra = {}) {
  const body = { ...extra };
  if (state.projectId) {
    body.cycleId = state.projectId;
  } else if (state.pbcprojectId) {
    body.pbcproject = state.pbcprojectId;
  }
  return body;
}

/**
 * Set project to the PBC project linked on the cycle (bookclosure_project.pbcproject).
 * The cycle id must stay on cycleId. Copying it into project fails the link check.
 * @param {{ pbcprojectId?: string }} state
 * @param {Object} payload
 * @param {string} [pbcProjectId]
 * @returns {Object}
 */
function withLinkedPbcProject(state, payload, pbcProjectId) {
  const cycleId = payload.cycleId ? String(payload.cycleId) : '';
  const explicit = pbcProjectId ? String(pbcProjectId) : '';
  const fromState = state.pbcprojectId ? String(state.pbcprojectId) : '';
  const linked = explicit && explicit !== cycleId ? explicit : fromState;
  if (linked && linked !== cycleId) {
    payload.project = linked;
  }
  return payload;
}

function initialState() {
  return {
    projectId: '',
    pbcprojectId: '',
    loading: false,
    uploadLoading: false,
    metadata: {},
    summary: {},
    users: [],
    error: null,
    requests: [],
    infoOwners: [],
    inactiveInfoOwners: [],
    locations: [],
    filteredLocations: [],
    projectSummary: {},
    emailSummaryTemplate: { subject: '', body: '', placeholders: [] },
    emailTemplateLoaded: false,
    completeChecklist: [],
    completeChecklistLoaded: false,
    listViewState: {},
  };
}

export default {
  namespaced: true,
  state: initialState(),
  getters: {
    projectId: state => state.projectId,
    isLoading: state => state.loading,
    isUploadLoading: state => state.uploadLoading,
    projectMetadata: state => state.metadata,
    summary: state => state.summary,
    userList: state => state.users,
    informationManagerError: state => state.error,
    requestList: state => state.requests,
    /** Active info owners only — used for request/task assignment. */
    infoOwnerList: state => state.infoOwners,
    /** Inactive info owners — used for enable/disable in the owners table. */
    inactiveInfoOwnerList: state => state.inactiveInfoOwners,
    /** Active + inactive — used for the owners list view. */
    allInfoOwnerList: state => [...(state.infoOwners || []), ...(state.inactiveInfoOwners || [])],
    locationList: state => state.locations,
    filteredLocationList: state => state.filteredLocations,
    projectSummary: state => state.projectSummary,
    emailSummaryTemplate: state => state.emailSummaryTemplate,
    completeChecklist: state => state.completeChecklist,
    requestById: (state) => (id) => state.requests.find((request) => request._id === id) || null,
    getListViewState: (state) => (projectId) => state.listViewState[projectId] || null,
  },
  mutations: {
    SET_PROJECT_ID(state, projectId) {
      state.projectId = projectId;
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_UPLOAD_LOADING(state, value) {
      state.uploadLoading = value;
    },
    SET_METADATA(state, metadata) {
      state.metadata = metadata;
      state.pbcprojectId = metadata.pbcproject;
    },
    SET_SUMMARY(state, summary) {
      state.summary = summary;
    },
    SET_USERS(state, users) {
      state.users = users;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_REQUESTS(state, requests) {
      state.requests = requests;
    },
    SET_INFO_OWNERS(state, infoOwners) {
      state.infoOwners = infoOwners || [];
    },
    SET_INACTIVE_INFO_OWNERS(state, inactiveInfoOwners) {
      state.inactiveInfoOwners = inactiveInfoOwners || [];
    },
    SET_LOCATIONS(state, locations) {
      state.locations = locations;
    },
    SET_FILTERED_LOCATIONS(state, locations) {
      state.filteredLocations = locations || [];
    },
    SET_PROJECT_SUMMARY(state, summary) {
      state.projectSummary = summary;
    },
    SET_EMAIL_SUMMARY_TEMPLATE(state, template) {
      state.emailSummaryTemplate = template || { subject: '', body: '', placeholders: [] };
      state.emailTemplateLoaded = true;
    },
    SET_COMPLETE_CHECKLIST(state, checklist) {
      state.completeChecklist = checklist || [];
      state.completeChecklistLoaded = true;
    },
    UPDATE_REQUEST_IN_LIST(state, updatedRequest) {
      const index = state.requests.findIndex(r => r._id === updatedRequest._id);
      if (index !== -1) {
        Vue.set(state.requests, index, updatedRequest);
      }
    },
    ADD_REQUEST_TO_LIST(state, newRequest) {
      state.requests.push(newRequest);
    },
    REMOVE_REQUEST_FROM_LIST(state, requestId) {
      state.requests = state.requests.filter(r => r._id !== requestId);
    },
    ADD_INFO_OWNER(state, infoOwner) {
      if (!infoOwner) return;
      if (infoOwner.status === false) {
        state.inactiveInfoOwners.push(infoOwner);
      } else {
        state.infoOwners.push(infoOwner);
      }
    },
    UPDATE_INFO_OWNER(state, updatedOwner) {
      if (!updatedOwner?._id) return;
      const ownerId = String(updatedOwner._id);
      state.infoOwners = state.infoOwners.filter((owner) => String(owner._id) !== ownerId);
      state.inactiveInfoOwners = state.inactiveInfoOwners.filter((owner) => String(owner._id) !== ownerId);
      if (updatedOwner.status === false) {
        state.inactiveInfoOwners.push(updatedOwner);
      } else {
        state.infoOwners.push(updatedOwner);
      }
    },
    REMOVE_INFO_OWNER(state, ownerId) {
      const id = String(ownerId);
      state.infoOwners = state.infoOwners.filter((owner) => String(owner._id) !== id);
      state.inactiveInfoOwners = state.inactiveInfoOwners.filter((owner) => String(owner._id) !== id);
    },
    SET_LIST_VIEW_STATE(state, { projectId, partial }) {
      if (!projectId) return;
      state.listViewState = {
        ...state.listViewState,
        [projectId]: {
          ...(state.listViewState[projectId] || {}),
          ...partial,
        },
      };
    },
    CLEAR_LIST_VIEW_STATE(state, projectId) {
      if (!projectId) return;
      const next = { ...state.listViewState };
      delete next[projectId];
      state.listViewState = next;
    },
    RESET_STATE(state) {
      Object.assign(state, initialState());
    },
  },
  actions: {
    async bulkUploadPbc({ commit, dispatch, state }, { projectId, items }) {
      commit('SET_UPLOAD_LOADING', true);
      try {
        const response = await Vue.axios.post(`/v2/financialclose/analysis/bulkaddpbc/${projectId}`, { data: items });
        if (response.data.status === 'success') {
          commit('sbSuccess', ERROR_MESSAGES.INFORMATION_REQUIREMENTS_UPLOADED, { root: true });
          // If pbcprojectId is not set (PBC project was just created by this upload),
          // refresh project context to get the new pbcprojectId
          if (!state.pbcprojectId) {
            await dispatch('fetchProjectContext', { projectId: projectId || state.projectId });
          }
          return 'success';
        } else {
          throw new Error(response.data.message || ERROR_MESSAGES.ERROR_UPLOADING_INFORMATION_REQUIREMENTS);
        }
      } catch (err) {
        commit('sbError', err?.response?.data?.message || err?.message || err || 'Unknown error!', { root: true });
        return 'error';
      } finally {
        commit('SET_UPLOAD_LOADING', false);
      }
    },

    /**
     * Dry-run IR bulk import validation (location/tags/actor).
     * @returns {{ valid: boolean, rowErrors: Array, message?: string }}
     */
    async validateBulkPbc(_ctx, { projectId, items }) {
      try {
        const response = await Vue.axios.post(
          `/v2/financialclose/analysis/validateBulkPbc/${projectId}`,
          { data: items },
        );
        const payload = response?.data?.data || {};
        return {
          valid: payload.valid === true,
          fieldErrors: payload.fieldErrors || {},
          rowErrors: Array.isArray(payload.rowErrors) ? payload.rowErrors : [],
          message: response?.data?.message,
        };
      } catch (err) {
        return {
          valid: false,
          fieldErrors: err?.response?.data?.data?.fieldErrors || {},
          rowErrors: err?.response?.data?.data?.rowErrors || [],
          message: err?.response?.data?.message || err?.message || 'Validation failed',
        };
      }
    },

    /**
     * V3 Page Load — Step 1+2 (parallel):
     * - /v3/financialclose/analysis/getdetails/:id (page: "im") → metadata
     * - /v3/financialclose/analysis/getformdata → users + locations (cached via tasks store)
     *
     * Replaces the old fetchProjectContext which called:
     * - /v2/financialclose/analysis/getdetails/:id (full payload with checklist, TB, pbcitems)
     * - /v2/financialclose/analysis/getformdata
     */
    async fetchProjectContext({ commit, dispatch }, { projectId } = {}) {
      if (!projectId) {
        const message = ERROR_MESSAGES.PROJECT_ID_REQUIRED;
        commit('SET_ERROR', message);
        commit('sbError', message, { root: true });
        return 'error';
      }

      const inFlightKey = String(projectId);
      if (fetchProjectContextInFlight && fetchProjectContextInFlightKey === inFlightKey) {
        return fetchProjectContextInFlight;
      }

      fetchProjectContextInFlightKey = inFlightKey;
      fetchProjectContextInFlight = (async () => {
        commit('SET_PROJECT_ID', projectId);
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
          // Parallel: v3/getdetails (slim metadata) + v3/getformdata (cached users+locations)
          const [detailsResponse, formData] = await Promise.all([
            getAxios().post(`/v3/financialclose/analysis/getdetails/${projectId}`),
            dispatch('tasks/fetchFormDataV3', {}, { root: true }),
          ]);

          // Handle detailsResponse (v3 returns flat data object, not data[0])
          const detailsStatus = detailsResponse?.data?.status;
          const detailsData = detailsResponse?.data?.data;
          if (detailsStatus !== 'success' || !detailsData) {
            throw new Error(detailsResponse?.data?.message || ERROR_MESSAGES.UNABLE_TO_LOAD_INFORMATION_MANAGER_DETAILS);
          }
          commit('SET_METADATA', detailsData.metadata || {});
          commit('SET_SUMMARY', detailsData.summary || {});
          commit('SET_FILTERED_LOCATIONS', detailsData.filteredLocations || []);

          // Handle formData (from tasks store cache)
          if (formData) {
            const nova = Vue.prototype?.$nova;
            const users = formData.users || [];
            const palette = nova?.getColors ? nova.getColors(users.length, 0.2) : [];
            const usersWithColors = mapInformationManagerUsers({ users }, palette);
            commit('SET_USERS', usersWithColors);
            commit('SET_LOCATIONS', formData.locations || []);
            if (formData.emailSummaryTemplate) {
              commit('SET_EMAIL_SUMMARY_TEMPLATE', formData.emailSummaryTemplate);
            }
          }

          return 'success';
        } catch (error) {
          const message = error?.response?.data?.message || error?.message || 'Unable to refresh Information Manager data';
          commit('SET_ERROR', message);
          commit('sbError', message, { root: true });
          return 'error';
        } finally {
          commit('SET_LOADING', false);
        }
      })().finally(() => {
        if (fetchProjectContextInFlightKey === inFlightKey) {
          fetchProjectContextInFlight = null;
          fetchProjectContextInFlightKey = null;
        }
      });

      return fetchProjectContextInFlight;
    },

    /**
     * Loads info owners and commits active/inactive lists (same split as Information Requests).
     * Used by task-detail add-request modal and other callers that do not need full request data.
     */
    async fetchInfoOwners({ commit, state }) {
      try {
        const infoOwnersRes = await getAxios().post(
          `${BASE_URL}infoowner/list`,
          withFcCycleContext(state)
        );
        if (infoOwnersRes.data.status === 'success') {
          const lists = normalizeInfoOwnerListPayload(infoOwnersRes.data.data);
          commitInfoOwnerLists(commit, lists);
          return lists.active;
        }
        console.error('[informationManager]: Failed to fetch info owners', infoOwnersRes.data.message);
        return [];
      } catch (error) {
        console.error('[informationManager]: fetchInfoOwners error', error);
        return [];
      }
    },

    /**
     * V3 Page Load — Step 3:
     * - /v3/pbc/project/getdata/:pbcProjectId → project summary + enriched requests
     * - /v2/pbc/infoowner/list → info owner dropdown (via fetchInfoOwners)
     *
     * Replaces the old fetchRequests which called 5 APIs:
     * - /v2/pbc/project/get/:id
     * - /v2/pbc/project/getrequests/:id
     * - /v2/pbc/infoowner/list
     * - /v2/financialclose/location/get (now from getformdata)
     * - /v2/pbc/project/getformdata (now lazy)
     */
    async fetchRequests({ commit, state }, { projectId } = {}) {
      const pbcProjectId = projectId || state.pbcprojectId;
      const cycleProjectId = state.projectId;
      const inFlightKey = String(pbcProjectId || cycleProjectId || 'none');

      if (fetchRequestsInFlight && fetchRequestsInFlightKey === inFlightKey) {
        return fetchRequestsInFlight;
      }

      fetchRequestsInFlightKey = inFlightKey;
      fetchRequestsInFlight = (async () => {
        commit('SET_LOADING', true);
        try {
          const infoOwnerListBody = withFcCycleContext({
            projectId: state.projectId,
            pbcprojectId: state.pbcprojectId || pbcProjectId,
          });

          const commitInfoOwnersFromOutcome = (outcome) => {
            if (outcome?.ok && outcome.res?.data?.status === 'success') {
              commitInfoOwnerLists(commit, outcome.res.data.data);
              return 'success';
            }
            const message = outcome?.ok
              ? outcome.res?.data?.message
              : (outcome?.error?.response?.data?.message || outcome?.error?.message);
            console.error('[informationManager]: Failed to fetch info owners', message || outcome?.error);
            return 'error';
          };

          const failRequests = (error) => {
            const data = error?.response?.data;
            const message = data?.message || error?.message || ERROR_MESSAGES.ERROR_FETCHING_DATA;
            const code = data?.code ?? error?.response?.status;
            if (isPbcProjectNotFoundMessage(message, code)) {
              commit('SET_PROJECT_SUMMARY', {});
              commit('SET_REQUESTS', []);
              return 'error';
            }
            commit('sbError', message, { root: true });
            return 'error';
          };

          if (pbcProjectId) {
            // Requests and info owners are independent: a getdata failure must not drop owners.
            const [dataOutcome, infoOwnersOutcome] = await Promise.all([
              settleAxios(getAxios().post(`/v3/pbc/project/getdata/${pbcProjectId}`)),
              settleAxios(getAxios().post(`${BASE_URL}infoowner/list`, infoOwnerListBody)),
            ]);

            const ownerStatus = commitInfoOwnersFromOutcome(infoOwnersOutcome);

            if (!dataOutcome.ok) {
              return failRequests(dataOutcome.error);
            }
            if (dataOutcome.res.data.status === 'success' && dataOutcome.res.data.data) {
              const { project, requests } = dataOutcome.res.data.data;
              commit('SET_PROJECT_SUMMARY', project || {});
              commit('SET_REQUESTS', requests || []);
            } else {
              return failRequests(new Error(dataOutcome.res.data.message || ERROR_MESSAGES.NO_PROJECT_FOUND));
            }

            if (ownerStatus === 'error') {
              commit('sbError', ERROR_MESSAGES.ERROR_FETCHING_INFORMATION_OWNER_LIST, { root: true });
              return 'error';
            }
            return 'success';
          }

          if (cycleProjectId) {
            // getrequests can 404 before a PBC project exists. That must not block infoowner/list.
            const [requestsOutcome, infoOwnersOutcome] = await Promise.all([
              settleAxios(getAxios().post(`${BASE_URL}project/getrequests/${cycleProjectId}`)),
              settleAxios(getAxios().post(`${BASE_URL}infoowner/list`, infoOwnerListBody)),
            ]);

            commitInfoOwnersFromOutcome(infoOwnersOutcome);

            if (!requestsOutcome.ok) {
              return failRequests(requestsOutcome.error);
            }

            const requestsRes = requestsOutcome.res;
            if (requestsRes.data.status === 'success') {
              commit('SET_PROJECT_SUMMARY', {});
              commit('SET_REQUESTS', requestsRes.data.data || []);
            } else if (isPbcProjectNotFoundResponse(requestsRes.data, requestsRes.status)) {
              // Expected when no PBC project exists yet — empty list, no toast.
              commit('SET_PROJECT_SUMMARY', {});
              commit('SET_REQUESTS', []);
            } else {
              return failRequests(new Error(requestsRes.data.message || ERROR_MESSAGES.ERROR_FETCHING_DATA));
            }

            return 'success';
          }

          // No cycle / PBC id in scope — leave body unchanged (known Phase E 403 risk)
          const infoOwnersOutcome = await settleAxios(getAxios().post(`${BASE_URL}infoowner/list`));
          commit('SET_PROJECT_SUMMARY', {});
          commit('SET_REQUESTS', []);
          commitInfoOwnersFromOutcome(infoOwnersOutcome);
          return 'success';
        } finally {
          commit('SET_LOADING', false);
        }
      })().finally(() => {
        if (fetchRequestsInFlightKey === inFlightKey) {
          fetchRequestsInFlight = null;
          fetchRequestsInFlightKey = null;
        }
      });

      return fetchRequestsInFlight;
    },

    /**
     * Lazy-load email summary template on first dialog open.
     * Replaces fetching /v2/pbc/project/getformdata on every page load.
     */
    async fetchEmailTemplate({ commit, state }) {
      if (state.emailTemplateLoaded) return state.emailSummaryTemplate;
      try {
        const res = await getAxios().post(`${BASE_URL}project/getformdata`);
        if (res.data.status === 'success') {
          const template = res.data.data?.[0]?.emailSummaryTemplate;
          if (template) {
            commit('SET_EMAIL_SUMMARY_TEMPLATE', template);
            return template;
          }
        }
        return state.emailSummaryTemplate;
      } catch (err) {
        console.error('[informationManager]: fetchEmailTemplate error', err);
        return state.emailSummaryTemplate;
      }
    },

    /**
     * Fetch a page of checklist tasks for the task picker in the request form.
     * Uses /v3/getchecklist/:id with pagination and optional text search.
     * Does NOT commit to store — returns data directly for component-local state.
     */
    async fetchChecklistPage(_, { projectId, pagination, search, searchFields, locationId }) {
      const body = {};
      if (pagination) body.pagination = pagination;
      if (search) {
        body.search = search;
        body.searchFields = searchFields || ['ref', 'description', 'locationName'];
      }
      if (locationId) {
        if (!body.filters) body.filters = {};
        body.filters.location = locationId;
      }
      const response = await getAxios().post(`/v3/financialclose/analysis/getchecklist/${projectId}`, body);
      if (response.data.status === 'success') {
        return response.data.data; // { records, totalCount, page, limit, totalPages }
      }
      throw new Error(response.data.message || 'Failed to fetch checklist page');
    },

    async fetchAllChecklist({ commit, state }, { projectId, locationId }) {
      // Return cached data only for unfiltered requests
      if (!locationId && state.completeChecklistLoaded && state.projectId === projectId) {
        return state.completeChecklist;
      }
      try {
        const body = {};
        if (locationId) {
          body.filters = { location: locationId };
        }
        const response = await getAxios().post(`/v3/financialclose/analysis/getchecklist/${projectId}`, body);
        if (response.data.status === 'success') {
          const checklist = response.data.data?.records || [];
          // Only cache the unfiltered result to avoid polluting the global cache
          // with a location-scoped subset
          if (!locationId) {
            commit('SET_COMPLETE_CHECKLIST', checklist);
          }
          return checklist;
        }
        throw new Error(response.data.message || 'Failed to fetch all checklist items');
      } catch (error) {
        console.error('[informationManager]: Error fetching all checklist:', error);
        return [];
      }
    },

    async changeRequestStage({ commit, dispatch }, { requestId, stage }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/changestage/${requestId}/${stage}`);
        if (res.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.STATUS_CHANGED || 'Status changed', { root: true });
          dispatch('fetchRequests'); // Refresh data
          return 'success';
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        commit('sbError', error?.response?.data?.message || error?.message || CYCLE_MESSAGES.ERROR.ERROR_CHANGING_STAGE || 'Error changing stage', { root: true });
        return 'error';
      }
    },
    async closeProject({ commit, dispatch }, { projectId }) {
      try {
        const res = await getAxios().post(`${BASE_URL}project/close/${projectId}`);
        if (res.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.PROJECT_CLOSED || 'Project Closed Successfully', { root: true });
          dispatch('fetchRequests');
          return 'success';
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        commit('sbError', error?.response?.data?.message || error?.message || CYCLE_MESSAGES.ERROR.ERROR_CLOSING_PROJECT || 'Error closing project', { root: true });
        return 'error';
      }
    },
    async openProject({ commit, dispatch }, { projectId }) {
      try {
        const res = await getAxios().post(`${BASE_URL}project/open/${projectId}`);
        if (res.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.PROJECT_REOPENED || 'Project re-opened Successfully', { root: true });
          dispatch('fetchRequests');
          return 'success';
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        commit('sbError', error?.response?.data?.message || error?.message || CYCLE_MESSAGES.ERROR.ERROR_REOPENING_PROJECT || 'Error opening project', { root: true });
        return 'error';
      }
    },
    async addRequest({ commit, dispatch, state }, { data }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/add/`, { data });
        if (res.data.status === 'success') {
          commit('sbSuccess', ERROR_MESSAGES.NEW_REQUEST_ADDED || 'New Request Added', { root: true });
          // If pbcprojectId is not set (PBC project was just created), refresh project context
          // to get the new pbcprojectId before fetching requests
          if (!state.pbcprojectId && state.projectId) {
            await dispatch('fetchProjectContext', { projectId: state.projectId });
          }
          await dispatch('fetchRequests');
          return { status: 'success', data: res.data.data || [] };
        } else {
          return { status: 'error', data: res.data.data };
        }
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || ERROR_MESSAGES.ERROR_ADDING_REQUEST || 'Error adding request';
        commit('sbError', message, { root: true });
        return { status: 'error', message };
      }
    },
    async editRequest({ commit, dispatch }, { requestId, data }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/edit/${requestId}`, { data });
        if (res.data.status === 'success') {
          commit('sbSuccess', ERROR_MESSAGES.REQUEST_UPDATED || 'Request Updated', { root: true });
          dispatch('fetchRequests');
          return { status: 'success' };
        } else {
          return { status: 'error', data: res.data.data };
        }
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || ERROR_MESSAGES.ERROR_EDITING_REQUEST || 'Error editing request';
        commit('sbError', message, { root: true });
        return { status: 'error', message };
      }
    },
    async deleteRequest({ commit, dispatch, state }, { requestId, cycleId }) {
      try {
        const resolvedCycleId = cycleId || state.projectId || '';
        const payload = withFcCycleContext(state, {
          data: resolvedCycleId ? { cycleId: resolvedCycleId } : {},
        });
        if (state.pbcprojectId && !payload.data.project) {
          payload.data = { ...(payload.data || {}), project: state.pbcprojectId };
        }
        const res = await getAxios().post(`${BASE_URL}request/delete/${requestId}`, payload);
        if (res.data.status === 'success') {
          commit('sbSuccess', ERROR_MESSAGES.REQUEST_DELETED || 'Request Deleted', { root: true });
          dispatch('fetchRequests');
          return 'success';
        } else {
          throw new Error(res.data.message || ERROR_MESSAGES.ERROR_DELETING_REQUEST || 'Error deleting request');
        }
      } catch (error) {
        commit('sbError', error?.response?.data?.message || error?.message || ERROR_MESSAGES.ERROR_DELETING_REQUEST || 'Error deleting request', { root: true });
        return 'error';
      }
    },
    async updateResponse({ commit, dispatch }, { requestId, data, files, client }) {
      try {
        const uploadedFileIds = [];
        if (Array.isArray(files) && files.length > 0) {
          const nova = Vue.prototype.$nova;
          const uploadResults = await Promise.all(files.filter(Boolean).map(async (file) => {
            const uploadRes = await nova.uploadFile(getAxios(), file);
            const items = uploadRes?.data?.data || [];
            if (uploadRes?.data?.status !== 'success' || items.length === 0) {
              throw new Error('Unable to upload file');
            }
            return items.map(item => item?._id).filter(Boolean);
          }));
          uploadResults.forEach(ids => uploadedFileIds.push(...ids));
        }

        const source = Array.isArray(data) ? (data[0] || {}) : (data || {});
        const payload = { ...source };
        if (payload.submit === undefined) {
          payload.submit = true;
        }
        if (payload.response === undefined || payload.response === null ||
          (typeof payload.response === 'string' && payload.response.trim() === '')) {
          payload.response = {};
        }

        const existingFileIds = Array.isArray(payload.fileid)
          ? payload.fileid.filter(Boolean)
          : [];
        if (Array.isArray(source.fileid) || uploadedFileIds.length > 0) {
          payload.fileid = [...existingFileIds, ...uploadedFileIds];
        }

        const requestBody = { data: payload };
        if (client) {
          requestBody.client = client;
        }

        const res = await getAxios().post(`${BASE_URL}request/updateresponse/${requestId}`, requestBody);
        if (res.data.status === 'success') {
          commit('sbSuccess', 'Response Updated', { root: true });
          await dispatch('fetchRequests');
          return 'success';
        }

        const error = new Error(res.data.message || 'Error updating response');
        if (res.data.data) error.data = res.data.data;
        throw error;
      } catch (error) {
        console.error('[informationManager]: updateResponse error', error);
        commit('sbError', error?.response?.data?.message || error?.message || 'Error updating response', { root: true });
        throw error;
      }
    },
    async bulkUpdateInfoOwner({ commit, dispatch }, { projectId, data }) {
      try {
        const res = await getAxios().post(`/v2/financialclose/pbc/request/bulkupdateinfoowner/${projectId}`, { data });
        if (res.data.status === 'success') {
          commit('sbSuccess', 'The Information Owner Email Successfully Replaced', { root: true });
          dispatch('fetchRequests');
          return 'success';
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        commit('sbError', error?.response?.data?.message || error?.message || 'Error updating info owners', { root: true });
        return 'error';
      }
    },
    async bulkCancelRequests({ commit, dispatch, rootState }, { projectId, lineIds }) {
      try {
        const ids = Array.isArray(lineIds) ? lineIds.filter(Boolean) : [];
        if (!projectId || ids.length === 0) {
          throw new Error('No requests selected to cancel');
        }
        const payload = {
          data: [{ lineids: ids }],
        };
        const client = rootState?.client || rootState?.user?.client;
        const clientId = typeof client === 'object' ? (client?._id || client?.id) : client;
        if (clientId) {
          payload.client = clientId;
        }
        const res = await getAxios().post(`${BASE_URL}request/bulkcancel/${projectId}`, payload);
        if (res.data.status === 'success') {
          commit('sbSuccess', 'Requests cancelled successfully', { root: true });
          await dispatch('fetchRequests');
          return 'success';
        }
        throw new Error(res.data.message || 'Error cancelling requests');
      } catch (error) {
        commit('sbError', error?.response?.data?.message || error?.message || 'Error cancelling requests', { root: true });
        return 'error';
      }
    },
    async addInfoOwner({ commit, state }, { data, pbcProjectId, projectId }) {
      try {
        const payload = withLinkedPbcProject(
          state,
          withFcCycleContext(state, { data }),
          pbcProjectId || projectId,
        );
        const response = await getAxios().post(`${BASE_URL}infoowner/add/`, payload);
        if (response.data.status === 'success' && response.data.data.length > 0) {
          const newOwner = response.data.data[0];
          commit('ADD_INFO_OWNER', newOwner);
          commit('sbSuccess', 'New Information Owner Added', { root: true });
          return response.data;
        }

        const message = response.data.message || 'Error adding info owner';
        const hasFieldErrors = Array.isArray(response.data.data) && response.data.data.length > 0;
        if (!hasFieldErrors) {
          commit('sbError', message, { root: true });
        }
        return { status: 'error', data: response.data.data, message };
      } catch (error) {
        const responseData = error?.response?.data;
        const message = responseData?.message || error?.message || 'Error adding info owner';
        const hasFieldErrors = Array.isArray(responseData?.data) && responseData.data.length > 0;
        if (!hasFieldErrors) {
          commit('sbError', message, { root: true });
        }
        return {
          status: 'error',
          message,
          data: responseData?.data,
        };
      }
    },
    async editInfoOwner({ commit, dispatch, state }, { id, data, pbcProjectId, projectId }) {
      commit('SET_LOADING', true);
      try {
        const payload = withLinkedPbcProject(
          state,
          withFcCycleContext(state, { data }),
          pbcProjectId || projectId,
        );
        const res = await getAxios().post(`${BASE_URL}infoowner/edit/${id}`, payload);
        if (res.data.status === 'success') {
          const payload = Array.isArray(data) ? (data[0] || {}) : (data || {});
          const responseOwner = Array.isArray(res.data.data) && res.data.data.length > 0
            ? res.data.data[0]
            : { _id: id, ...payload };
          const updatedOwner = {
            ...responseOwner,
            _id: responseOwner._id || id,
            // Prefer response status; fall back to request payload so toggles move between lists
            status: responseOwner.status !== undefined
              ? responseOwner.status !== false
              : payload.status !== false,
          };
          commit('UPDATE_INFO_OWNER', updatedOwner);
          commit('sbSuccess', 'Updated Owner Information', { root: true });
          // Re-fetch requests + infoowner/list so enable and disable both refresh from server
          await dispatch('fetchRequests');
          return { status: 'success', data: res.data.data || [updatedOwner] };
        }
        commit('SET_LOADING', false);
        return { status: 'error', data: res.data.data, message: res.data.message };
      } catch (error) {
        commit('SET_LOADING', false);
        const message = error?.response?.data?.message || error?.message || 'Error editing info owner';
        commit('sbError', message, { root: true });
        return { status: 'error', message };
      }
    },
    async deleteInfoOwner({ commit, state }, { id, pbcProjectId, projectId }) {
      try {
        const payload = withLinkedPbcProject(
          state,
          withFcCycleContext(state, { data: {} }),
          pbcProjectId || projectId,
        );
        const res = await getAxios().post(`${BASE_URL}infoowner/delete/${id}`, payload);
        if (res.data.status === 'success') {
          commit('REMOVE_INFO_OWNER', id);
          commit('sbSuccess', res.data.message || 'Information owner deleted', { root: true });
          return { status: 'success' };
        }
        throw new Error(res.data.message || 'Error deleting information owner');
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Error deleting information owner';
        commit('sbError', message, { root: true });
        return { status: 'error', message, data: error?.response?.data?.data };
      }
    },
    downloadInfoManagerTemplate() {
      const nova = Vue.prototype?.$nova;
      if (nova?.downloadFile) {
        nova.downloadFile('', {}, '/v2/financialclose/analysis/getinfomanagertemplate');
      }
      return 'success';
    },
    saveListViewState({ commit }, { projectId, partial }) {
      commit('SET_LIST_VIEW_STATE', { projectId, partial });
      return 'success';
    },
    clearListViewState({ commit }, projectId) {
      commit('CLEAR_LIST_VIEW_STATE', projectId);
      return 'success';
    },
    async addComment({ commit }, { projectId, data }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/addcomment/${projectId}`, { data });
        if (res.data.status === 'success' && res.data.data.length > 0) {
          commit('sbSuccess', 'Comment Added', { root: true });
          return { status: 'success', data: res.data.data[0] };
        } else {
          return { status: 'error', data: res.data.data };
        }
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Error adding comment';
        commit('sbError', message, { root: true });
        return { status: 'error', message };
      }
    },
    async triggerTaggedNotification({ commit, rootState, state }, {
      projectId,
      cycleId,
      requestId,
      commentText,
      taggedUsers,
      requestMeta = {},
    }) {
      try {
        const client = rootState.client;
        const currentUserId = rootState.user?.id || rootState.user?._id || '';
        const infoOwners = state.infoOwners || [];
        const users = (taggedUsers || [])
          .filter((user) => user?.email && String(user._id || user.id) !== String(currentUserId))
          .map((user) => ({
            name: user.name || user.email,
            email: user.email,
            requestUrl: user.requestUrl || buildTaggedUserRequestUrl({
              userId: user._id || user.id,
              client,
              cycleId,
              requestId,
              pbcProjectId: projectId,
              infoOwners,
            }),
          }));

        if (!users.length) {
          return { success: true };
        }

        const payload = {
          data: {
            commenter: rootState.user?.name || rootState.user?.email || 'User',
            commentText,
            requestId,
            requestUrl: buildTaggedUserRequestUrl({
              userId: null,
              client,
              cycleId,
              requestId,
              pbcProjectId: projectId,
              infoOwners: [],
            }),
            requestSerial: requestMeta.serial || '',
            requestDescription: requestMeta.description || '',
            infoOwnerName: requestMeta.infoOwner || '',
            locationName: requestMeta.location || '',
            dueDate: requestMeta.dueDate || '',
            taggedUsers: users,
          },
        };

        await getAxios().post(`${BASE_URL}request/triggerTaggedNotification/${projectId}`, payload, {
          headers: { 'Content-Type': 'application/json' },
        });
        return { success: true };
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Failed to send tagged notification';
        console.error('[informationManager]: triggerTaggedNotification error', error);
        commit('sbError', message, { root: true });
        return { success: false, message };
      }
    },
    async markCommentAsRead({ commit }, { requestId }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/markcommentasread/${requestId}`);
        if (res.data.status === 'success') {
          return 'success';
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        commit('sbError', error?.response?.data?.message || error?.message || 'Error marking comment as read', { root: true });
        return 'error';
      }
    },
    async sendMail({ commit, dispatch }, { projectId, data }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/sendmail/${projectId}`, { data });
        if (res.data.status === 'success') {
          commit('sbSuccess', 'Mail sent', { root: true });
          dispatch('fetchRequests');
          return 'success';
        } else {
          throw new Error(res.data.message || 'Error sending mail');
        }
      } catch (error) {
        commit('sbError', error?.response?.data?.message || error?.message || 'Error sending mail', { root: true });
        return 'error';
      }
    },
    async fetchEmailSuggestions({ state }, { projectId } = {}) {
      try {
        const pid = projectId || state.pbcprojectId;
        if (!pid) return [];
        const response = await getAxios().post(`/v2/pbc/project/getemailsuggestions/${pid}`);
        if (response.data.status === 'success') {
          return response.data.data?.[0]?.suggestions || [];
        }
        return [];
      } catch (error) {
        console.error('[informationManager]: fetchEmailSuggestions error', error);
        return [];
      }
    },
    async triggerEmailSummary({ commit }, { projectId, selectedItems, to, cc, body, subject }) {
      try {
        const response = await getAxios().post(`/v2/pbc/project/triggeremailsummary`, {
          data: [{
            project: projectId,
            pbc_requests: selectedItems,
            to_emails: to,
            cc_emails: cc || [],
            body: body,
            subject: subject,
          }],
        });
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Email summary has been initiated successfully', { root: true });
          return 'success';
        } else {
          throw new Error(response.data.message || 'Failed to trigger email summary');
        }
      } catch (error) {
        console.error('[StoreAction]: triggerEmailSummary error', error);
        const rawMessage = error?.response?.data?.message || error?.message;
        const message = (typeof rawMessage === 'string') ? rawMessage : 'Failed to trigger email summary';
        commit('sbError', message, { root: true });
        return 'error';
      }
    },
    async uploadBulkRequirements({ state, commit, dispatch }, { projectId, items } = {}) {
      const targetProjectId = projectId || state.projectId;
      if (!targetProjectId) {
        const message = 'Project id is required to upload requirements';
        commit('sbError', message, { root: true });
        return 'error';
      }
      if (!Array.isArray(items) || items.length === 0) {
        const message = 'No Information Manager requirements provided for upload';
        commit('sbError', message, { root: true });
        return 'error';
      }

      commit('SET_UPLOAD_LOADING', true);
      try {
        const response = await getAxios().post(`/v2/financialclose/analysis/bulkaddpbc/${targetProjectId}`, { data: items });
        if (response?.data?.status !== 'success') {
          throw new Error(response?.data?.message || 'Error uploading Information Manager requirements');
        }
        commit('sbSuccess', 'Information Requirements uploaded successfully', { root: true });
        await dispatch('fetchProjectContext', { projectId: targetProjectId });
        return 'success';
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Unknown error uploading Information Manager requirements';
        commit('SET_ERROR', message);
        commit('sbError', message, { root: true });
        return 'error';
      } finally {
        commit('SET_UPLOAD_LOADING', false);
      }
    },
    resetState({ commit }) {
      commit('RESET_STATE');
      return 'success';
    },
  },
};
